import { ReactElement, ReactNode } from 'react';
import { useWizard } from 'react-use-wizard';
import Box from '@ramsey-design-system/box';
import ProgressBar from '@ramsey-design-system/progress-bar';
import { MatcherBackChevron } from './MatcherBackChevron';
import { MatcherSkipButton } from './MatcherSkipButton';
import { useMatcherContext } from '../context';
import { MAX_STEP_ORDER } from '../flow/buildStepQueue';

interface Props {
  children: ReactNode;
  hideProgressBar?: boolean;
  onSkip?: () => void | Promise<void>;
  skipButtonText?: string;
  skipDisabled?: boolean;
  // Overrides the chevron's default single-step previousStep() - e.g. MatcherNameStep uses
  // this to jump back over MatcherSearchStep (a loading step with nothing to land on) straight
  // to MatcherMeetingStep.
  onBack?: () => void;
}

function MatcherStepWrapper({
  children,
  hideProgressBar = false,
  onSkip,
  skipButtonText = 'Skip',
  skipDisabled = false,
  onBack
}: Readonly<Props>): ReactElement {
  const { activeStep } = useWizard();
  const { stepQueue } = useMatcherContext();
  // Measured against the fixed MAX_STEP_ORDER, not stepQueue.length, so progress never appears
  // to move backward as branching answers change the fan's own queue length.
  const currentStepIndex = Math.max(
    0,
    MAX_STEP_ORDER.indexOf(stepQueue[activeStep])
  );
  const totalSteps = MAX_STEP_ORDER.length;

  return (
    <div data-testid={'matcher-step-wrapper'} className="flex justify-center">
      <Box className="w-full max-w-[418px]" hasBorder={true} padding="lg">
        <div className="flex justify-between items-center mb-4">
          <MatcherBackChevron
            dataTestID="tax_matcher_back_button"
            onBack={onBack}
          />
          {!hideProgressBar && (
            <ProgressBar
              value={((currentStepIndex + 1.0) / (totalSteps + 0.5)) * 100}
              className={'ml-4 mr-4'}
              size={'large'}
            />
          )}
          <div className="min-w-1.75 min-h-1.75" />
        </div>
        {children}
        {onSkip && (
          <MatcherSkipButton
            onSkip={onSkip}
            buttonText={skipButtonText}
            disabled={skipDisabled}
          />
        )}
      </Box>
    </div>
  );
}

export { MatcherStepWrapper };
